import {
  SCOUT_WRITE_MODE_GQL,
  SCOUT_WRITE_MODE_OPTIONS,
  type ScoutWriteModeGql,
} from "./scoutWriteMode";

export const SCOUT_WRITE_ACCESS_CONTROL_LABEL = "Scout data changes";

export const SCOUT_WRITE_ACCESS_INFO_TOOLTIP =
  "Choose whether Scout can change your Encamp data using your permissions: Data editing off (read-only), Ask before editing, or Data editing on.";

export type ScoutDataChangesChipColor = "default" | "teal" | "warning";

export type ScoutDataChangesIndicator = {
  label: string;
  /** Short trigger label for tight surfaces (popover header). */
  shortLabel: string;
  color: ScoutDataChangesChipColor;
  description: string;
};

export function getScoutDataChangesIndicator(
  writeMode: ScoutWriteModeGql
): ScoutDataChangesIndicator {
  if (writeMode === SCOUT_WRITE_MODE_GQL.AUTO) {
    return {
      label: "Data editing on",
      shortLabel: "On",
      color: "teal",
      description: "Data editing on. Scout can apply data changes immediately. Click to change.",
    };
  }
  if (writeMode === SCOUT_WRITE_MODE_GQL.ASK) {
    return {
      label: "Ask before editing",
      shortLabel: "Ask",
      color: "warning",
      description:
        "Ask before editing. Scout will propose data changes for your review. Click to change.",
    };
  }
  return {
    label: "Data editing off",
    shortLabel: "Off",
    color: "default",
    description: "Data editing off. Scout cannot change Encamp data. Click to change.",
  };
}

export type UseScoutWriteAccessControlInput = {
  writeMode: ScoutWriteModeGql;
  disabled?: boolean;
  hasScoutProAccess?: boolean;
};

export function useScoutWriteAccessControl(input: UseScoutWriteAccessControlInput) {
  const hasScoutProAccess = input.hasScoutProAccess ?? true;
  const isFeatureEnabled = hasScoutProAccess;
  const disabled = input.disabled ?? false;
  const indicator = getScoutDataChangesIndicator(input.writeMode);

  return {
    isFeatureEnabled,
    writeMode: input.writeMode,
    disabled,
    controlLabel: SCOUT_WRITE_ACCESS_CONTROL_LABEL,
    infoTooltip: SCOUT_WRITE_ACCESS_INFO_TOOLTIP,
    options: SCOUT_WRITE_MODE_OPTIONS,
    indicatorLabel: indicator.label,
    indicatorShortLabel: indicator.shortLabel,
    indicatorColor: indicator.color,
    indicatorTooltip: indicator.description,
    indicatorAriaLabel: indicator.description,
  };
}
